import type { AdminUser, AreasOverview, ContractListItem, ForecastGap, ForecastOverview, InvoiceItem, Issue, MarginOverview, Me, OrderScanStatus, Overview, ProjectOverview, WorkdaySyncRun, WorkdaySyncStatus } from '@ft/shared';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLoad } from '../components/ui';
import { useI18n } from '../i18n';
import { getAreaId, getAreaName, switchArea } from '../lib/area';
import { dfmt, dtfmt, eur } from '../lib/format';
import { issueText } from './IssuesPage';

/**
 * Startseite als Kacheln (Bento), je nach Sicht: Delivery Head, Admin, Finance, PM.
 * Die Sicht folgt der Rolle; wer mehrere Sichten hat, kann umschalten (der Browser merkt sich die Wahl).
 * Alle Zahlen kommen aus den bestehenden Schnittstellen; der Server prüft wie immer die Rechte.
 */
export type StartView = 'dh' | 'admin' | 'finance' | 'pm';
const VIEW_KEY = 'ft.start.view';

export function viewsFor(me: Me): StartView[] {
  const v: StartView[] = [];
  if (me.deliveryHead) v.push('dh');
  if (me.role === 'admin') v.push('admin');
  if (me.role === 'admin' || me.role === 'finance' || me.deliveryHead) v.push('finance');
  if (me.role === 'admin' || me.role === 'pm') v.push('pm');
  return v;
}

const COL = ['var(--acc-1)', 'var(--acc-2)', 'var(--acc-3)', 'var(--acc-4)', 'var(--acc-7)', 'var(--acc-5)'];
type L = (de: string, en: string) => string;

export function StartBento({ me }: { me: Me }) {
  const { t, lang } = useI18n();
  const L: L = (de, en) => (lang === 'en' ? en : de);
  const views = viewsFor(me);
  const [view, setView] = useState<StartView>(() => {
    let saved: string | null = null;
    try { saved = localStorage.getItem(VIEW_KEY); } catch { /* egal */ }
    return views.includes(saved as StartView) ? (saved as StartView) : views[0]!;
  });
  const pick = (v: StartView) => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* egal */ } };

  const year = new Date().getFullYear();
  const can = (s: string) => me.sections.includes(s as never);
  const { data: ov } = useLoad<Overview>(`/api/overview?year=${year}`);
  const { data: issues } = useLoad<Issue[]>(can('issues') ? '/api/issues' : null);
  const { data: inv } = useLoad<InvoiceItem[]>(can('invoicing') ? '/api/invoices' : null);
  const { data: gap } = useLoad<ForecastGap>(can('invoicing') ? '/api/invoices/forecast-gap' : null);
  const { data: fc } = useLoad<ForecastOverview>(can('forecast') ? `/api/forecast?year=${year}` : null);
  const { data: mg } = useLoad<MarginOverview>(me.role !== 'reader' && view !== 'pm' ? '/api/margin' : null);
  const { data: areas } = useLoad<AreasOverview>(view === 'dh' ? `/api/overview/areas?year=${year}` : null);
  const { data: wd } = useLoad<WorkdaySyncStatus>(view === 'admin' || view === 'finance' ? (can('invoicing') ? '/api/workday/sync' : null) : null);
  const { data: scan } = useLoad<OrderScanStatus>(view === 'admin' && can('orders') ? '/api/orders/scan' : null);
  const { data: users } = useLoad<AdminUser[]>(view === 'admin' ? '/api/admin/users' : null);
  const { data: contracts } = useLoad<ContractListItem[]>(view === 'admin' && can('contracts') ? '/api/contracts' : null);
  const { data: errs } = useLoad<{ rows: { at: string; status: number }[] }>(view === 'admin' && me.logAccess ? '/api/activity?mode=errors' : null);

  const first = me.name.split(' ')[0] ?? me.name;
  const today = new Date();
  const dateLine = today.toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const label: Record<StartView, string> = { dh: 'Delivery Head', admin: 'Admin', finance: 'Finance', pm: 'PM' };
  const sub: Record<StartView, string> = {
    dh: L(`Delivery Head · ${me.areas.length} Bereiche`, `Delivery Head · ${me.areas.length} areas`),
    admin: L(`Admin · Bereich ${getAreaName()} · Zahlen, Datenqualität und Betrieb`, `Admin · area ${getAreaName()} · figures, data quality and operations`),
    finance: L(`Finance · Bereich ${getAreaName()} · Zahlungen, Forderungen und was noch fakturiert werden muss`, `Finance · area ${getAreaName()} · payments, receivables and what still needs invoicing`),
    pm: L('PM · deine Projekte als PM oder Vertretung', 'PM · your projects as PM or deputy'),
  };

  return (
    <section className="page sb">
      <div className="sb-head">
        <div>
          <div className="sb-date">{dateLine}</div>
          <h1>{t('start.hello', { name: first })}</h1>
          <div className="sb-sub">{sub[view]}</div>
        </div>
        {views.length > 1 && (
          <div className="sb-seg" role="group" aria-label={L('Sicht', 'View')}>
            {views.map((v) => <button key={v} type="button" aria-pressed={v === view} onClick={() => pick(v)}>{label[v]}</button>)}
          </div>
        )}
      </div>
      {!ov ? <div className="sb-loading muted">{t('app.loading')}</div> : (
        <Views view={view} me={me} L={L} lang={lang} ov={ov} issues={issues} inv={inv} gap={gap} fc={fc} mg={mg} areas={areas} wd={wd} scan={scan} users={users} contracts={contracts} errs={errs?.rows ?? null} />
      )}
    </section>
  );
}

type Data = {
  view: StartView; me: Me; L: L; lang: string; ov: Overview; issues: Issue[] | null; inv: InvoiceItem[] | null; gap: ForecastGap | null; fc: ForecastOverview | null;
  mg: MarginOverview | null; areas: AreasOverview | null; wd: WorkdaySyncStatus | null; scan: OrderScanStatus | null; users: AdminUser[] | null; contracts: ContractListItem[] | null; errs: { at: string; status: number }[] | null;
};

function Views(d: Data) {
  const { view, me, L, ov } = d;
  const { t } = useI18n();
  const isMine = (p: { pm: { id: string } | null; deputy: { id: string } | null }) => p.pm?.id === me.id || p.deputy?.id === me.id;
  const mine = ov.projects.filter(isMine);
  const projects = view === 'pm' ? mine : ov.projects;
  const pids = new Set(projects.map((p) => p.id));
  const invs = (d.inv ?? []).filter((i) => view !== 'pm' || pids.has(i.project.id));
  const claims = invs.filter((i) => !i.warranty);
  const unpaid = claims.filter((i) => i.payment?.status === 'unpaid');
  const overdue = unpaid.filter((i) => i.payment!.overdueDays);
  const open = unpaid.reduce((s, i) => s + i.payment!.openNetCents, 0);
  const overSum = overdue.reduce((s, i) => s + i.payment!.openNetCents, 0);
  const oldest = overdue.reduce((m, i) => Math.max(m, i.payment!.overdueDays ?? 0), 0);
  const gapRows = (d.gap?.projects ?? []).filter((p) => view !== 'pm' || pids.has(p.projectId));
  const toInvoice = gapRows.reduce((s, p) => s + Math.max(0, p.gapCents), 0);
  const nextFrom = d.gap?.nextFrom ?? null;
  const issues = (d.issues ?? []).filter((i) => view !== 'pm' || i.pms.some((p) => p.id === me.id)).filter((i) => me.role === 'admin' || !i.link.startsWith('/verwaltung'));
  const risky = projects.filter((p) => p.health === 'behind' || p.health === 'uncovered');
  const sum = (f: (p: ProjectOverview) => number) => projects.reduce((s, p) => s + f(p), 0);
  const goal = { target: sum((p) => p.targetCents ?? 0), ordered: sum((p) => p.orderedCents), billed: sum((p) => p.billedCents) };
  const canInv = me.sections.includes('invoicing');
  const canFc = me.sections.includes('forecast');
  const cal = <CalendarTile L={L} lang={d.lang} nextFrom={nextFrom} cls="s2" />;

  if (view === 'dh') {
    const a = d.areas?.areas ?? [];
    const tot = { target: a.reduce((s, x) => s + x.targetCents, 0), ordered: a.reduce((s, x) => s + x.orderedCents, 0), billed: a.reduce((s, x) => s + x.billedCents, 0) };
    return (
      <div className="sb-grid">
        <GoalTile L={L} title={L(`Jahresziel ${ov.year} · alle Bereiche`, `Target ${ov.year} · all areas`)} {...(d.areas ? tot : goal)} pace={ov.pace} />
        {canInv && <OutstandingTile L={L} open={open} gap={toInvoice} nextFrom={nextFrom} />}
        {canInv && <OverdueTile L={L} sum={overSum} n={overdue.length} oldest={oldest} cls="s2" />}
        {d.mg && <MarginTile L={L} mg={d.mg} />}
        {d.areas && <AreasTile L={L} data={d.areas} />}
        {canFc && d.fc && <MonthsTile L={L} lang={d.lang} fc={d.fc} cls="s3 r2" />}
        <RiskTile L={L} list={risky} pace={ov.pace} cls="s3 r2" />
        {canFc && d.fc && <AccountsTile L={L} fc={d.fc} cls="s3" />}
        {d.issues && <TodayTile L={L} issues={issues} cls="s3" />}
        {cal}
        <QuartersTile L={L} ov={ov} cls="s4" />
      </div>
    );
  }

  if (view === 'admin') {
    const code = (c: string) => issues.filter((i) => i.code === c).length;
    const quality: [string, number, 'crit' | 'neu'][] = [
      [L('Bestellungen nicht auf Projekte verteilt', 'Orders not allocated to projects'), code('notAllocated') + code('noLines'), 'crit'],
      [L('Verträge ohne Budget', 'Contracts without budget'), (d.contracts ?? []).filter((c) => c.state === 'noBudget').length, 'crit'],
      [L('Rechnungen ohne Bestellung', 'Invoices without order'), invs.filter((i) => !i.order && !i.warranty).length, 'neu'],
      [L('Projekte ohne PM', 'Projects without PM'), ov.projects.filter((p) => !p.pm).length, 'neu'],
      [L('Projekte ohne Ziel', 'Projects without target'), ov.projects.filter((p) => p.targetCents === null).length, 'neu'],
    ];
    return (
      <div className="sb-grid">
        <GoalTile L={L} title={L(`Jahresziel ${ov.year} · ${getAreaName()}`, `Target ${ov.year} · ${getAreaName()}`)} {...goal} pace={ov.pace} />
        {canInv && <OutstandingTile L={L} open={open} gap={toInvoice} nextFrom={nextFrom} />}
        {canInv && <OverdueTile L={L} sum={overSum} n={overdue.length} oldest={oldest} cls="s2" />}
        {d.mg && <MarginTile L={L} mg={d.mg} />}
        <SyncTile L={L} wd={d.wd} fc={d.fc?.sync ?? null} scan={d.scan} />
        <Tile cls="s2" title={L('Datenqualität', 'Data quality')} link={['/offene-punkte', t('nav.issues')]}>
          {quality.map(([txt, n, tone]) => <Row key={txt} pill={<Pill tone={n ? tone : 'pos'}>{n}</Pill>} text={txt} />)}
        </Tile>
        <PeopleTile L={L} users={d.users} errs={d.errs} logAccess={me.logAccess} />
        {canFc && d.fc && <MonthsTile L={L} lang={d.lang} fc={d.fc} cls="s3 r2" />}
        <RiskTile L={L} list={risky} pace={ov.pace} cls="s3 r2" />
        {d.issues && <TodayTile L={L} issues={issues} cls="s3" />}
        {canFc && d.fc && <AccountsTile L={L} fc={d.fc} cls="s3" />}
      </div>
    );
  }

  if (view === 'finance') {
    const paid = claims.filter((i) => i.payment?.status === 'paid');
    return (
      <div className="sb-grid">
        <ReceivablesTile L={L} open={open} openN={unpaid.length} over={overSum} overN={overdue.length} oldest={oldest} paid={paid.reduce((s, i) => s + i.amountCents, 0)} />
        <AgingTile L={L} unpaid={unpaid} />
        <OutstandingTile L={L} open={open} gap={toInvoice} nextFrom={nextFrom} />
        <OpenInvoicesTile L={L} unpaid={unpaid} />
        <ByCustomerTile L={L} unpaid={unpaid} />
        <ToInvoiceTile L={L} rows={gapRows} total={toInvoice} nextFrom={nextFrom} cls="s3 r2" />
        <PaymentsTile L={L} invs={claims} cls="s3" />
        <BilledMonthsTile L={L} lang={d.lang} invs={invs} year={ov.year} />
        <Tile cls="s3 tint-pos" title={L('Workday-Abgleich', 'Workday sync')} link={['/invoicing', 'Invoicing']}>
          <RunInfo L={L} run={d.wd?.last ?? null} />
          <div className="sb-small">{L(`${invs.filter((i) => !i.order && !i.warranty).length} Rechnungen ohne Bestellung · ${invs.filter((i) => i.warranty).length} Warranty · ${claims.filter((i) => !i.payment).length} ohne Zahlungsstatus`, `${invs.filter((i) => !i.order && !i.warranty).length} invoices without order · ${invs.filter((i) => i.warranty).length} warranty · ${claims.filter((i) => !i.payment).length} without payment status`)}</div>
        </Tile>
        <CalendarTile L={L} lang={d.lang} nextFrom={nextFrom} cls="s3" />
      </div>
    );
  }

  // PM
  const paidRecent = claims.filter((i) => i.payment?.status === 'paid' && i.payment.paidAt && daysAgo(i.payment.paidAt) <= 14);
  return (
    <div className="sb-grid">
      <GoalTile L={L} title={L(`Meine Projekte · Ziel ${ov.year}`, `My projects · target ${ov.year}`)} {...goal} pace={ov.pace} />
      {canInv && <OutstandingTile L={L} open={open} gap={toInvoice} nextFrom={nextFrom} />}
      {canInv && <OverdueTile L={L} sum={overSum} n={overdue.length} oldest={oldest} cls="s2" />}
      {canInv && (
        <Tile cls="s1 tint-pos" title={L('Eingegangen', 'Received')}>
          <div className="sb-mid num pos">{kEur(paidRecent.reduce((s, i) => s + i.amountCents, 0), L)}</div>
          <div className="sb-small">{L(`letzte 14 Tage · ${paidRecent.length} Zahlungen`, `last 14 days · ${paidRecent.length} payments`)}</div>
        </Tile>
      )}
      <Tile cls="s3 r2" title={L('Meine Projekte', 'My projects')} link={['/projekte', t('nav.projects')]}>
        {mine.length ? mine.map((p) => <ProjRow key={p.id} p={p} pace={ov.pace} L={L} />) : <div className="muted sb-small">{L('Du bist bei keinem Projekt PM oder Vertretung.', 'You are not PM or deputy of any project.')}</div>}
      </Tile>
      {d.issues && <TodayTile L={L} issues={issues} cls="s3" title={L('Heute zu tun', 'To do today')} />}
      {canInv && <PaymentsTile L={L} invs={claims} cls="s3" />}
      {canInv && <ToInvoiceTile L={L} rows={gapRows} total={toInvoice} nextFrom={nextFrom} cls="s3" />}
      <CalendarTile L={L} lang={d.lang} nextFrom={nextFrom} cls="s3" />
    </div>
  );
}

/* ---------- Bausteine ---------- */
const daysAgo = (iso: string) => Math.floor((Date.now() - new Date(`${iso.slice(0, 10)}T12:00:00`).getTime()) / 86_400_000);
const mio = (c: number, L: L) => `${(c / 100_000_000).toLocaleString(L('de-DE', 'en-GB'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${L('Mio €', '€m')}`;
const kEur = (c: number, L: L) => `${Math.round(c / 100_000).toLocaleString(L('de-DE', 'en-GB'))} ${L('T€', 'k€')}`;
const pc = (v: number) => `${Math.round(v * 100)} %`;

function Tile({ cls, title, link, children }: { cls: string; title: ReactNode; link?: [string, string]; children: ReactNode }) {
  return (
    <div className={`sb-tile ${cls}`}>
      <div className="sb-lbl">{title}{link && <Link to={link[0]}>{link[1]} →</Link>}</div>
      {children}
    </div>
  );
}
function Pill({ tone, children }: { tone: 'pos' | 'neg' | 'crit' | 'info' | 'neu'; children: ReactNode }) { return <span className={`pill ${tone}`}>{children}</span>; }
function Row({ pill, text, amt, onClick }: { pill?: ReactNode; text: ReactNode; amt?: string; onClick?: () => void }) {
  return (
    <div className={`sb-row${onClick ? ' click' : ''}`} onClick={onClick} onKeyDown={(e) => { if (onClick && e.key === 'Enter') onClick(); }} tabIndex={onClick ? 0 : undefined}>
      {pill}<span className="t">{text}</span>{amt && <span className="amt num">{amt}</span>}
    </div>
  );
}

function GoalTile({ L, title, target, ordered, billed, pace }: { L: L; title: string; target: number; ordered: number; billed: number; pace: number }) {
  const r = target ? billed / target : 0;
  const diff = Math.round((r - pace) * 100);
  return (
    <div className="sb-tile sb-dark s3 r2">
      <div className="sb-lbl">{title}</div>
      <div className="sb-big num">{(billed / 100_000_000).toLocaleString(L('de-DE', 'en-GB'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="of">/ {target ? mio(target, L) : L('kein Ziel', 'no target')}</span></div>
      {target > 0 && <div className="sb-small">{L(`${pc(r)} abgerechnet · Soll heute ${pc(pace)} · `, `${pc(r)} billed · expected today ${pc(pace)} · `)}<span className={diff >= 0 ? 'up' : 'down'}>{diff >= 0 ? '+' : '−'}{Math.abs(diff)} {L(`Pkt. ${diff >= 0 ? 'vor' : 'hinter'} Plan`, `pts ${diff >= 0 ? 'ahead of' : 'behind'} plan`)}</span></div>}
      <div className="sb-kv">
        <div><div className="sb-lbl">{L('Bestellt', 'Ordered')}</div><div className="v num">{mio(ordered, L)}</div></div>
        <div><div className="sb-lbl">{L('Offenes Volumen', 'Open volume')}</div><div className="v num">{mio(Math.max(0, ordered - billed), L)}</div></div>
      </div>
      <svg className="sb-gauge" viewBox="0 0 270 270" aria-hidden="true">
        <defs><linearGradient id="sbg"><stop offset="0" stopColor="#1C8AD6" /><stop offset="1" stopColor="#6fe0a8" /></linearGradient></defs>
        <circle cx="135" cy="135" r="100" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="22" />
        <circle cx="135" cy="135" r="100" fill="none" stroke="url(#sbg)" strokeWidth="22" strokeLinecap="round" strokeDasharray="628" strokeDashoffset={628 - 628 * Math.min(1, r)} transform="rotate(-90 135 135)" />
        <line x1="135" y1="35" x2="135" y2="13" stroke="#fff" strokeWidth="3" transform={`rotate(${pace * 360} 135 135)`} />
      </svg>
    </div>
  );
}

function OutstandingTile({ L, open, gap, nextFrom }: { L: L; open: number; gap: number; nextFrom: string | null }) {
  const tot = open + gap || 1;
  return (
    <Tile cls="s3 tint-info" title={L('Ausstehend', 'Outstanding')} link={['/invoicing', 'Invoicing']}>
      <div className="sb-line"><div className="sb-mid num info">{kEur(open + gap, L)}</div>
        <span className="sb-small">{L(`= offen ${kEur(open, L)} + noch zu fakturieren ${kEur(gap, L)}`, `= open ${kEur(open, L)} + still to invoice ${kEur(gap, L)}`)}{nextFrom ? L(` · nächster Monat ab ${dfmt(nextFrom)}`, ` · next month from ${dfmt(nextFrom)}`) : ''}</span></div>
      <div className="sb-stack"><i style={{ width: `${(open / tot) * 100}%`, background: 'var(--accent)' }} /><i style={{ width: `${(gap / tot) * 100}%`, background: 'var(--sb-light)' }} /></div>
      <div className="sb-legend"><span style={{ ['--c' as string]: 'var(--accent)' }}>{L('offen (Rechnungen gestellt)', 'open (invoiced)')}</span><span style={{ ['--c' as string]: 'var(--sb-light)' }}>{L('noch zu fakturieren laut Forecast', 'still to invoice per forecast')}</span></div>
    </Tile>
  );
}

function OverdueTile({ L, sum, n, oldest, cls }: { L: L; sum: number; n: number; oldest: number; cls: string }) {
  return (
    <Tile cls={`${cls} ${n ? 'tint-neg' : 'tint-pos'}`} title={L('Überfällig', 'Overdue')} link={['/invoicing?zahlung=overdue', L('Anzeigen', 'Show')]}>
      <div className={`sb-mid num ${n ? 'neg' : 'pos'}`}>{kEur(sum, L)}</div>
      <div className="sb-small">{n ? L(`${n} Rechnungen · älteste ${oldest} Tage`, `${n} invoices · oldest ${oldest} days`) : L('nichts überfällig', 'nothing overdue')}</div>
    </Tile>
  );
}

function MarginTile({ L, mg }: { L: L; mg: MarginOverview }) {
  const ns = mg.total.netSalesCents;
  const p = ns ? ((ns - mg.total.costCents) / ns) * 100 : 0;
  const ok = p >= mg.areaTarget;
  const months = mg.closedMonths.map((m) => mg.months[m - 1]!).map((v) => (v.netSalesCents ? ((v.netSalesCents - v.costCents) / v.netSalesCents) * 100 : 0));
  return (
    <Tile cls={`s1 ${ok ? 'tint-pos' : 'tint-crit'}`} title={L('Marge', 'Margin')} link={['/marge', L('Öffnen', 'Open')]}>
      <div className="sb-mid num">{ns ? `${p.toLocaleString(L('de-DE', 'en-GB'), { maximumFractionDigits: 1 })} %` : '–'}</div>
      <div className={`sb-small ${ok ? 'pos' : 'crit'}`}>{L('Ziel', 'Target')} {mg.areaTarget} %{ok ? ' ✓' : ''}</div>
      <div className="sb-spark">{months.map((m, i) => <i key={i} title={`${m.toFixed(1)} %`} style={{ height: `${Math.max(10, Math.min(100, ((m - 25) / 35) * 100))}%`, background: m >= mg.areaTarget ? 'var(--pos)' : m >= mg.areaTarget - 10 ? 'var(--acc-4)' : 'var(--neg)' }} />)}</div>
    </Tile>
  );
}

function MonthsTile({ L, lang, fc, cls }: { L: L; lang: string; fc: ForecastOverview; cls: string }) {
  const f = fc.totals.forecastCents, a = fc.totals.actualCents, cm = fc.currentMonth;
  const mx = Math.max(1, ...f);
  const mn = (i: number) => new Date(Date.UTC(2026, i, 1)).toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-DE', { month: 'short', timeZone: 'UTC' });
  return (
    <Tile cls={cls} title={L(`Umsatz je Monat ${fc.year} · Forecast und Ist`, `Revenue per month ${fc.year} · forecast and actual`)} link={['/forecast', 'Forecast']}>
      <div className="sb-chart">
        {f.map((v, i) => (
          <div key={i} className="m" title={`${mn(i)}: Forecast ${eur(v)} · ${L('Ist', 'Actual')} ${eur(a[i] ?? 0)}`}>
            <div className={`bar${i === cm ? ' now' : i > cm ? ' fut' : ''}`} style={{ height: `${(v / mx) * 100}%` }}>
              {i <= cm && (a[i] ?? 0) > 0 && <span className="ist" style={{ bottom: `${Math.min(100, ((a[i] ?? 0) / Math.max(v, 1)) * 100)}%` }} />}
            </div>
            <span className="ml">{mn(i).slice(0, 1)}</span>
          </div>
        ))}
      </div>
      <div className="sb-legend"><span style={{ ['--c' as string]: 'var(--sb-light)' }}>Forecast</span><span style={{ ['--c' as string]: 'var(--accent)' }}>{L('laufender Monat', 'current month')}</span><span style={{ ['--c' as string]: 'var(--fg)' }}>{L('Ist (Strich)', 'actual (line)')}</span></div>
      <div className="sb-small" style={{ marginTop: 8 }}>Forecast {mio(fc.totals.forecastTotalCents, L)} · {L('Ist', 'Actual')} {mio(fc.totals.actualTotalCents, L)} · {L('erwartet', 'expected')} {mio(fc.totals.expectedTotalCents, L)}</div>
    </Tile>
  );
}

function AccountsTile({ L, fc, cls }: { L: L; fc: ForecastOverview; cls: string }) {
  const by = new Map<string, number>();
  for (const p of fc.projects) by.set(p.customer.name, (by.get(p.customer.name) ?? 0) + p.forecastTotalCents);
  const list = [...by].filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const tot = list.reduce((s, x) => s + x[1], 0) || 1;
  return (
    <Tile cls={cls} title={L(`Forecast je Account ${fc.year}`, `Forecast per account ${fc.year}`)}>
      <div className="sb-stack big">{list.map(([n, v], i) => <i key={n} title={`${n}: ${eur(v)}`} style={{ width: `${(v / tot) * 100}%`, background: COL[i % COL.length] }} />)}</div>
      <div className="sb-legend">{list.map(([n, v], i) => <span key={n} style={{ ['--c' as string]: COL[i % COL.length] }}>{n} {kEur(v, L)} · {Math.round((v / tot) * 100)} %</span>)}</div>
    </Tile>
  );
}

function ProjRow({ p, pace, L }: { p: ProjectOverview; pace: number; L: L }) {
  const nav = useNavigate();
  const base = Math.max(p.targetCents ?? 0, p.orderedCents, 1);
  const go = () => nav(`/bestellungen?projekt=${encodeURIComponent(p.name)}`);
  return (
    <div className="sb-proj click" tabIndex={0} onClick={go} onKeyDown={(e) => { if (e.key === 'Enter') go(); }}>
      <div className="n"><b>{p.name}</b><span className="sb-small">{p.customer.name}{p.pm ? ` · ${p.pm.name}` : ''}</span></div>
      <div>
        <div className="sb-meter"><i className="o" style={{ width: `${(p.orderedCents / base) * 100}%` }} /><i className="b" style={{ width: `${(p.billedCents / base) * 100}%` }} />{p.targetCents ? <span className="pc" style={{ left: `${Math.min(100, ((p.targetCents * pace) / base) * 100)}%` }} /> : null}</div>
        <div className="sb-small num">{eur(p.billedCents)} {L('von', 'of')} {p.targetCents ? eur(p.targetCents) : '–'}</div>
      </div>
      {p.health === 'onTrack' ? <Pill tone="pos">{L('Im Plan', 'On track')}</Pill> : p.health === 'behind' ? <Pill tone="crit">{L('Hinter Soll', 'Behind')}</Pill> : p.health === 'uncovered' ? <Pill tone="neg">{L('Ohne Deckung', 'Uncovered')}</Pill> : <Pill tone="neu">{L('Ohne Ziel', 'No target')}</Pill>}
    </div>
  );
}

function RiskTile({ L, list, pace, cls }: { L: L; list: ProjectOverview[]; pace: number; cls: string }) {
  return (
    <Tile cls={cls} title={L(`Projekte mit Risiko · ${list.length}`, `Projects at risk · ${list.length}`)} link={['/projekte', L('Projekte & Ziele', 'Projects')]}>
      {list.length ? [...list].sort((a, b) => (b.targetCents ?? 0) - (a.targetCents ?? 0)).slice(0, 7).map((p) => <ProjRow key={p.id} p={p} pace={pace} L={L} />) : <div className="sb-small pos">{L('Alle Projekte im Plan.', 'All projects on track.')}</div>}
    </Tile>
  );
}

function TodayTile({ L, issues, cls, title }: { L: L; issues: Issue[]; cls: string; title?: string }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const rank = { neg: 0, crit: 1, info: 2 } as const;
  const list = [...issues].sort((a, b) => rank[a.level] - rank[b.level]).slice(0, 5);
  return (
    <Tile cls={cls} title={title ?? L('Heute wichtig', 'Important today')} link={['/offene-punkte', t('nav.issues')]}>
      {list.length ? list.map((i) => {
        const x = issueText(i, t);
        return <Row key={i.key} pill={<Pill tone={i.level === 'info' ? 'neu' : i.level}>{t(`issues.level.${i.level}`)}</Pill>} text={x.title} onClick={() => nav(i.link)} />;
      }) : <div className="sb-small pos">{L('Nichts offen.', 'Nothing open.')}</div>}
      {issues.length > list.length && <div className="sb-small" style={{ marginTop: 6 }}>{L(`und ${issues.length - list.length} weitere`, `and ${issues.length - list.length} more`)}</div>}
    </Tile>
  );
}

function CalendarTile({ L, lang, nextFrom, cls }: { L: L; lang: string; nextFrom: string | null; cls: string }) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  const days = new Date(y, m + 1, 0).getDate();
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const key = `${y}-${String(m + 1).padStart(2, '0')}`;
  const billDay = nextFrom && nextFrom.startsWith(key) ? Number(nextFrom.slice(8, 10)) : null;
  const heads = lang === 'en' ? ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] : ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const title = now.toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-DE', { month: 'long', year: 'numeric' });
  return (
    <Tile cls={`${cls} tint-crit`} title={title}>
      <div className="sb-cal">
        {heads.map((h) => <span key={h} className="h">{h}</span>)}
        {Array.from({ length: lead }, (_, i) => <span key={`b${i}`} />)}
        {Array.from({ length: days }, (_, i) => {
          const d = i + 1, wd = (lead + i) % 7;
          return <span key={d} className={d === now.getDate() ? 'today' : d === billDay ? 'bill' : wd >= 5 ? 'we' : ''}>{d}</span>;
        })}
      </div>
      <div className="sb-legend" style={{ marginTop: 8 }}>
        {billDay && <span style={{ ['--c' as string]: 'var(--accent)' }}>{L(`${billDay}.: Fakturierung (letzter Arbeitstag)`, `${billDay}: invoicing (last working day)`)}</span>}
        <span style={{ ['--c' as string]: 'var(--fg)' }}>{L('heute', 'today')}</span>
      </div>
    </Tile>
  );
}

function QuartersTile({ L, ov, cls }: { L: L; ov: Overview; cls: string }) {
  const nav = useNavigate();
  const nowQ = Math.floor(new Date().getMonth() / 3) + 1;
  const mx = Math.max(1, ...ov.quarters.map((q) => q.billedCents));
  return (
    <Tile cls={cls} title={L(`Abgerechnet je Quartal ${ov.year}`, `Billed per quarter ${ov.year}`)}>
      <div className="sb-quarters">
        {ov.quarters.map((q) => (
          <button key={q.q} type="button" className={q.q === nowQ ? 'now' : q.q > nowQ ? 'fut' : ''} onClick={() => nav(`/invoicing?quartal=${ov.year}-Q${q.q}`)} title={q.byCustomer.map((c) => `${c.name}: ${eur(c.cents)}`).join('\n')}>
            <b className="num">{q.billedCents ? kEur(q.billedCents, L) : '–'}</b>
            <span className="bar" style={{ height: `${(q.billedCents / mx) * 100}%` }} />
            <span className="sb-small">Q{q.q}{q.q === nowQ ? L(' bisher', ' so far') : ''}</span>
          </button>
        ))}
      </div>
    </Tile>
  );
}

function AreasTile({ L, data }: { L: L; data: AreasOverview }) {
  const { t } = useI18n();
  const current = getAreaId();
  return (
    <section className="sb-tile s6" aria-labelledby="sb-areas-h">
      <div className="sb-lbl" id="sb-areas-h">{t('dh.title', { y: data.year })}</div>
      <div className="tablewrap"><table className="sb-table">
        <thead><tr><th>{t('app.area')}</th><th className="r">{L('Ziel', 'Target')}</th><th className="r">{L('Bestellt', 'Ordered')}</th><th className="r">{L('Abgerechnet', 'Billed')}</th><th>{L('Zielerreichung', 'Progress')}</th><th className="r">{L('Offenes Volumen', 'Open volume')}</th><th className="r">{L('Risiko', 'At risk')}</th><th>{t('nav.issues')}</th></tr></thead>
        <tbody>{data.areas.map((a) => {
          const r = a.targetCents ? a.billedCents / a.targetCents : null;
          return (
            <tr key={a.id} className={`click${a.id === current ? ' current' : ''}`} tabIndex={0} onClick={() => switchArea(a.id, '/')} onKeyDown={(e) => { if (e.key === 'Enter') switchArea(a.id, '/'); }}>
              <td><b>{a.name}</b>{!a.member && <span className="tag">{t('area.viewOnly')}</span>}{a.id === current && <span className="tag">{t('dh.current')}</span>}</td>
              <td className="r num">{a.targetCents ? eur(a.targetCents) : '–'}</td>
              <td className="r num">{eur(a.orderedCents)}</td>
              <td className="r num">{eur(a.billedCents)}</td>
              <td style={{ minWidth: 150 }}>{r === null ? <span className="sb-small">{L('kein Ziel', 'no target')}</span> : <><div className="sb-meter"><i className="b" style={{ width: `${Math.min(100, r * 100)}%`, background: r < data.pace * 0.9 ? 'var(--crit)' : 'var(--accent)' }} /><span className="pc" style={{ left: `${Math.min(100, data.pace * 100)}%` }} /></div><span className="sb-small">{pc(r)} · {L('Soll', 'expected')} {pc(data.pace)}</span></>}</td>
              <td className="r num">{eur(a.openCents)}</td>
              <td className="r">{a.projectsAtRisk ? <Pill tone="crit">{a.projectsAtRisk}</Pill> : '–'}</td>
              <td>{a.urgentIssues > 0 && <Pill tone="neg">{t('dh.urgent', { n: a.urgentIssues })}</Pill>} {a.checkIssues > 0 && <Pill tone="crit">{t('dh.check', { n: a.checkIssues })}</Pill>}{!a.urgentIssues && !a.checkIssues && '–'}</td>
            </tr>
          );
        })}</tbody>
      </table></div>
    </section>
  );
}

function RunInfo({ L, run }: { L: L; run: WorkdaySyncRun | null }) {
  if (!run) return <div className="sb-small">{L('Noch kein Abgleich.', 'No sync yet.')}</div>;
  const at = run.finishedAt ?? run.requestedAt;
  return (
    <>
      <div className="sb-mid num" style={{ fontSize: 22 }}>{at ? dtfmt(at) : '–'}</div>
      <div className="sb-small">{run.status === 'failed' ? L('fehlgeschlagen', 'failed') : L(`${run.newCount} neu · ${run.unmatchedCount} nicht zugeordnet`, `${run.newCount} new · ${run.unmatchedCount} unmatched`)}</div>
    </>
  );
}

function SyncTile({ L, wd, fc, scan }: { L: L; wd: WorkdaySyncStatus | null; fc: WorkdaySyncStatus | null; scan: OrderScanStatus | null }) {
  const line = (name: string, s: WorkdaySyncStatus | null, maxDays: number) => {
    const run = s?.last ?? null;
    const at = run?.finishedAt ?? null;
    const age = at ? daysAgo(at) : null;
    const tone = !run ? 'neu' : run.status === 'failed' ? 'neg' : age !== null && age > maxDays ? 'crit' : 'pos';
    const txt = !run ? L('noch nie', 'never') : run.status === 'failed' ? L('fehlgeschlagen', 'failed') : age !== null && age > maxDays ? L(`${age} Tage alt`, `${age} days old`) : 'ok';
    return <Row key={name} pill={<Pill tone={tone}>{txt}</Pill>} text={`${name}${at ? ` · ${dtfmt(at)}` : ''}`} />;
  };
  return (
    <Tile cls="s2" title={L('Abgleiche', 'Syncs')}>
      {wd && line('Workday', wd, 2)}
      {fc && line('Forecast', fc, 7)}
      {scan && scan.folders.length > 0 && line(L('Ordner-Scan', 'Folder scan'), scan.status, 7)}
    </Tile>
  );
}

function PeopleTile({ L, users, errs, logAccess }: { L: L; users: AdminUser[] | null; errs: { at: string; status: number }[] | null; logAccess: boolean }) {
  const act = (users ?? []).filter((u) => u.active);
  const n = (f: (u: AdminUser) => boolean) => act.filter(f).length;
  const week = (errs ?? []).filter((e) => daysAgo(e.at) <= 7);
  const c = (s: (x: number) => boolean) => week.filter((e) => s(e.status)).length;
  return (
    <Tile cls="s2" title={L('Personen & Betrieb', 'People & operations')} link={['/verwaltung/personen', L('Verwaltung', 'Admin')]}>
      <div className="sb-line"><div className="sb-mid num">{act.length}</div><span className="sb-small">{L('aktive Personen', 'active people')}</span></div>
      <div className="sb-legend">
        <span style={{ ['--c' as string]: 'var(--acc-1)' }}>{n((u) => u.role === 'admin')} Admin</span>
        <span style={{ ['--c' as string]: 'var(--acc-3)' }}>{n((u) => u.role === 'pm')} PM</span>
        <span style={{ ['--c' as string]: 'var(--acc-7)' }}>{n((u) => u.role === 'finance')} Finance</span>
        <span style={{ ['--c' as string]: 'var(--faint)' }}>{n((u) => u.role === 'reader')} {L('Lesend', 'Read-only')}</span>
        <span style={{ ['--c' as string]: 'var(--acc-2)' }}>{n((u) => u.deliveryHead)} Delivery Head</span>
      </div>
      {logAccess && errs && (
        <>
          <div className="sb-lbl" style={{ marginTop: 12 }}>{L('Protokoll · 7 Tage', 'Activity log · 7 days')}</div>
          <Row pill={<Pill tone={c((s) => s >= 500) ? 'neg' : 'pos'}>{c((s) => s >= 500)}</Pill>} text={L('Serverfehler', 'Server errors')} />
          <Row pill={<Pill tone={c((s) => s === 409) ? 'crit' : 'pos'}>{c((s) => s === 409)}</Pill>} text={L('Konflikte (gleichzeitig bearbeitet)', 'Conflicts (edited at the same time)')} />
          <Row pill={<Pill tone="neu">{c((s) => s === 401)}</Pill>} text={L('abgelaufene Anmeldungen', 'expired sign-ins')} />
        </>
      )}
    </Tile>
  );
}

function ReceivablesTile({ L, open, openN, over, overN, oldest, paid }: { L: L; open: number; openN: number; over: number; overN: number; oldest: number; paid: number }) {
  const share = paid + open ? paid / (paid + open) : 0;
  return (
    <div className="sb-tile sb-dark s3 r2">
      <div className="sb-lbl">{L('Forderungen · netto', 'Receivables · net')}</div>
      <div className="sb-big num">{kEur(open, L)} <span className="of">{L('offen', 'open')}</span></div>
      <div className="sb-small">{L(`${openN} Rechnungen · davon `, `${openN} invoices · of which `)}<span className="down">{kEur(over, L)} {L('überfällig', 'overdue')}</span> ({overN})</div>
      <div className="sb-kv">
        <div><div className="sb-lbl">{L('Bezahlt', 'Paid')}</div><div className="v num">{mio(paid, L)}</div></div>
        <div><div className="sb-lbl">{L('Quote', 'Rate')}</div><div className="v num">{pc(share)}</div></div>
        <div><div className="sb-lbl">{L('Älteste', 'Oldest')}</div><div className="v num">{oldest ? L(`${oldest} Tage`, `${oldest} days`) : '–'}</div></div>
      </div>
      <svg className="sb-gauge" viewBox="0 0 270 270" aria-hidden="true">
        <defs><linearGradient id="sbf"><stop offset="0" stopColor="#1C8AD6" /><stop offset="1" stopColor="#6fe0a8" /></linearGradient></defs>
        <circle cx="135" cy="135" r="100" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="22" />
        <circle cx="135" cy="135" r="100" fill="none" stroke="url(#sbf)" strokeWidth="22" strokeLinecap="round" strokeDasharray="628" strokeDashoffset={628 - 628 * share} transform="rotate(-90 135 135)" />
      </svg>
    </div>
  );
}

function AgingTile({ L, unpaid }: { L: L; unpaid: InvoiceItem[] }) {
  const od = (i: InvoiceItem) => i.payment?.overdueDays ?? 0;
  const b: [string, InvoiceItem[], string][] = [
    [L('nicht fällig', 'not due'), unpaid.filter((i) => !od(i)), 'var(--accent)'],
    [L('1–30 Tage', '1–30 days'), unpaid.filter((i) => od(i) > 0 && od(i) <= 30), 'var(--acc-4)'],
    [L('31–60 Tage', '31–60 days'), unpaid.filter((i) => od(i) > 30 && od(i) <= 60), 'var(--neg)'],
    [L('61–90 Tage', '61–90 days'), unpaid.filter((i) => od(i) > 60 && od(i) <= 90), 'var(--neg)'],
    [L('über 90 Tage', 'over 90 days'), unpaid.filter((i) => od(i) > 90), 'var(--neg)'],
  ];
  const s = (xs: InvoiceItem[]) => xs.reduce((x, i) => x + (i.payment?.openNetCents ?? 0), 0);
  const mx = Math.max(1, ...b.map((x) => s(x[1])));
  return (
    <Tile cls="s3" title={L('Offen nach Fälligkeit', 'Open by due date')} link={['/invoicing?zahlung=unpaid', 'Invoicing']}>
      {b.map(([l, xs, c]) => (
        <div key={l} className="sb-aging"><span className="sb-small">{l}</span><div className="sb-meter big"><i className="b" style={{ width: `${(s(xs) / mx) * 100}%`, background: c }} /></div><span className="num r sb-small">{xs.length ? `${eur(s(xs))} · ${xs.length}` : '–'}</span></div>
      ))}
    </Tile>
  );
}

function OpenInvoicesTile({ L, unpaid }: { L: L; unpaid: InvoiceItem[] }) {
  const nav = useNavigate();
  const list = [...unpaid].sort((a, b) => (b.payment?.overdueDays ?? 0) - (a.payment?.overdueDays ?? 0)).slice(0, 10);
  return (
    <Tile cls="s3 r2" title={L(`Offene Rechnungen · ${unpaid.length}`, `Open invoices · ${unpaid.length}`)} link={['/invoicing?zahlung=unpaid', 'Invoicing']}>
      {list.length ? list.map((i) => {
        const d = i.payment?.overdueDays ?? 0;
        return <Row key={i.id} pill={<Pill tone={d > 60 ? 'neg' : d > 0 ? 'crit' : 'info'}>{d ? L(`${d} Tage`, `${d} days`) : L('nicht fällig', 'not due')}</Pill>} text={`${i.project.name} · …${i.invoiceNumber.slice(-6)}`} amt={eur(i.payment?.openNetCents ?? i.amountCents)} onClick={() => nav(`/invoicing?q=${encodeURIComponent(i.invoiceNumber)}`)} />;
      }) : <div className="sb-small pos">{L('Keine offenen Rechnungen.', 'No open invoices.')}</div>}
      {unpaid.length > list.length && <div className="sb-small" style={{ marginTop: 6 }}>{L(`und ${unpaid.length - list.length} weitere`, `and ${unpaid.length - list.length} more`)}</div>}
    </Tile>
  );
}

function ByCustomerTile({ L, unpaid }: { L: L; unpaid: InvoiceItem[] }) {
  const by = new Map<string, number>();
  for (const i of unpaid) by.set(i.customer.name, (by.get(i.customer.name) ?? 0) + (i.payment?.openNetCents ?? 0));
  const list = [...by].sort((a, b) => b[1] - a[1]);
  const tot = list.reduce((s, x) => s + x[1], 0) || 1;
  return (
    <Tile cls="s3" title={L('Offen je Kunde', 'Open per customer')}>
      <div className="sb-stack big">{list.map(([n, v], i) => <i key={n} style={{ width: `${(v / tot) * 100}%`, background: COL[i % COL.length] }} />)}</div>
      <div className="sb-legend">{list.map(([n, v], i) => <span key={n} style={{ ['--c' as string]: COL[i % COL.length] }}>{n} {eur(v)}</span>)}</div>
    </Tile>
  );
}

function ToInvoiceTile({ L, rows, total, nextFrom, cls }: { L: L; rows: ForecastGap['projects']; total: number; nextFrom: string | null; cls: string }) {
  const list = rows.filter((p) => p.gapCents > 0).sort((a, b) => b.gapCents - a.gapCents);
  return (
    <Tile cls={cls} title={L('Noch zu fakturieren · Forecast bis heute minus Rechnungen', 'Still to invoice · forecast to date minus invoices')} link={['/invoicing', 'Invoicing']}>
      <div className="sb-mid num">{eur(total)}</div>
      {list.slice(0, 6).map((p) => <Row key={p.projectId} pill={<Pill tone="info">{p.customer}</Pill>} text={p.project} amt={eur(p.gapCents)} />)}
      {nextFrom && <div className="sb-small" style={{ marginTop: 6 }}>{L(`Der nächste Monat zählt ab ${dfmt(nextFrom)}.`, `The next month counts from ${dfmt(nextFrom)}.`)}</div>}
    </Tile>
  );
}

function PaymentsTile({ L, invs, cls }: { L: L; invs: InvoiceItem[]; cls: string }) {
  const list = invs.filter((i) => i.payment?.status === 'paid' && i.payment.paidAt).sort((a, b) => b.payment!.paidAt!.localeCompare(a.payment!.paidAt!)).slice(0, 5);
  return (
    <Tile cls={cls} title={L('Zahlungen eingegangen', 'Payments received')} link={['/invoicing?zahlung=paid', 'Invoicing']}>
      {list.length ? list.map((i) => <Row key={i.id} pill={<Pill tone="pos">{dfmt(i.payment!.paidAt).slice(0, 6)}</Pill>} text={`${i.project.name} · …${i.invoiceNumber.slice(-6)}`} amt={eur(i.amountCents)} />) : <div className="sb-small">{L('Noch keine Zahlungen erfasst.', 'No payments recorded yet.')}</div>}
    </Tile>
  );
}

function BilledMonthsTile({ L, lang, invs, year }: { L: L; lang: string; invs: InvoiceItem[]; year: number }) {
  const m = useMemo(() => { const a = Array<number>(12).fill(0); for (const i of invs) if (i.invoiceDate.startsWith(String(year))) a[Number(i.invoiceDate.slice(5, 7)) - 1]! += i.amountCents; return a; }, [invs, year]);
  const cm = new Date().getFullYear() === year ? new Date().getMonth() : 11;
  const mx = Math.max(1, ...m);
  const mn = (i: number) => new Date(Date.UTC(2026, i, 1)).toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-DE', { month: 'short', timeZone: 'UTC' });
  return (
    <Tile cls="s6" title={L(`Abgerechnet je Monat ${year}`, `Billed per month ${year}`)}>
      <div className="sb-chart low">
        {m.map((v, i) => <div key={i} className="m" title={`${mn(i)}: ${eur(v)}`}><div className={`bar solid${i === cm ? ' now' : i > cm ? ' fut' : ''}`} style={{ height: `${Math.max(2, (v / mx) * 100)}%` }} /><span className="ml">{mn(i)}</span></div>)}
      </div>
    </Tile>
  );
}
